import { useState } from "react";
import { playNow, kickAudio } from "@/audio/piano";

/**
 * MelodicCurvePlayer — renders a melodic line as a curve (the way Jason
 * draws them on his iPad), with each note plotted by pitch on the y-axis
 * and time on the x-axis. Click any dot to hear that note; click ▶ Play
 * to walk the whole curve at a steady tempo.
 *
 * The visual is the lesson — students learn to "read" pitch direction
 * before they learn note names.
 */

export interface CurveNote {
  /** Note name like "A4", "C#5". */
  pitch: string;
  /** Optional label shown at the dot (defaults to the pitch letter without octave). */
  label?: string;
}

interface MelodicCurvePlayerProps {
  notes: CurveNote[];
  /** ms between notes when playing the curve. Default 600. */
  noteDurationMs?: number;
  /** Optional title shown above the curve. */
  title?: string;
}

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

function pitchToMidi(pitch: string): number {
  // Accept "C4", "C#4", "Db4", "B♭4" etc.
  const m = pitch.match(/^([A-G])([#b♯♭]?)(-?\d+)$/);
  if (!m) return 60;
  const letter = m[1];
  const accidental = m[2];
  const octave = parseInt(m[3], 10);
  let semi = NOTE_NAMES.indexOf(letter);
  if (accidental === "#" || accidental === "♯") semi += 1;
  if (accidental === "b" || accidental === "♭") semi -= 1;
  return (octave + 1) * 12 + semi;
}

function shortLabel(pitch: string): string {
  return pitch.replace(/-?\d+$/, "");
}

export function MelodicCurvePlayer({
  notes,
  noteDurationMs = 600,
  title,
}: MelodicCurvePlayerProps) {
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [playing, setPlaying] = useState(false);

  if (notes.length === 0) return null;

  // Compute pitch bounds for vertical positioning
  const midis = notes.map((n) => pitchToMidi(n.pitch));
  const minMidi = Math.min(...midis);
  const maxMidi = Math.max(...midis);
  const range = Math.max(1, maxMidi - minMidi);

  // SVG geometry
  const padX = 40;
  const padY = 30;
  const width = Math.max(360, notes.length * 60);
  const height = 200;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const xFor = (i: number) =>
    notes.length === 1 ? padX + innerW / 2 : padX + (i / (notes.length - 1)) * innerW;
  const yFor = (midi: number) =>
    padY + innerH - ((midi - minMidi) / range) * innerH;

  const playOne = (i: number) => {
    setActiveIndex(i);
    void playNow(notes[i].pitch, "2n", 0.85);
    setTimeout(() => setActiveIndex(-1), noteDurationMs);
  };

  const playCurve = () => {
    if (playing) return;
    kickAudio();
    setPlaying(true);
    notes.forEach((n, i) => {
      setTimeout(() => {
        setActiveIndex(i);
        void playNow(n.pitch, "2n", 0.85);
      }, i * noteDurationMs);
    });
    setTimeout(() => {
      setActiveIndex(-1);
      setPlaying(false);
    }, notes.length * noteDurationMs);
  };

  // Path for the connecting line
  const points = notes
    .map((n, i) => `${xFor(i)},${yFor(pitchToMidi(n.pitch))}`)
    .join(" ");

  return (
    <div className="my-5 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.04] to-zinc-900/[0.04] p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300">
            Melodic curve
          </div>
          {title && <div className="mt-0.5 text-sm font-medium text-nsm-ink">{title}</div>}
        </div>
        <button
          onClick={playCurve}
          disabled={playing}
          className="rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-bold text-zinc-950 hover:bg-emerald-400 disabled:opacity-50"
        >
          {playing ? "● Playing…" : "▶ Play curve"}
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-emerald-500/20 bg-[#fafdf9]/[0.04] py-2">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width={width}
          height={height}
          className="block min-w-full"
        >
          {/* Axis line */}
          <line
            x1={padX} y1={height - padY}
            x2={width - padX} y2={height - padY}
            stroke="#10b98144"
            strokeWidth="1"
          />
          {/* Connecting curve */}
          <polyline
            points={points}
            fill="none"
            stroke="#10b981"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.7"
          />
          {/* Note dots + labels */}
          {notes.map((n, i) => {
            const cx = xFor(i);
            const cy = yFor(pitchToMidi(n.pitch));
            const isActive = activeIndex === i;
            return (
              <g key={i} className="cursor-pointer" onClick={() => playOne(i)}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={isActive ? 9 : 6}
                  fill={isActive ? "#fbbf24" : "#10b981"}
                  stroke="#fff"
                  strokeWidth="1.5"
                  className="transition-all"
                />
                <text
                  x={cx}
                  y={cy + 24}
                  textAnchor="middle"
                  className={`select-none font-mt text-[14px] ${isActive ? "fill-amber-300" : "fill-zinc-300"}`}
                  style={{ pointerEvents: "none" }}
                >
                  {n.label ?? shortLabel(n.pitch)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="mt-2 text-[11px] text-nsm-dim">
        Click any dot to hear it. Click <em>Play curve</em> to walk the whole melody.
      </div>
    </div>
  );
}
